
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronRight } from "lucide-react";
import { CrmWebhookDialog } from "./CrmWebhookDialog";

interface CrmIntegrationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

const crms = [
  {
    name: "Salesforce",
    description: "Connect with the world's #1 CRM platform",
    logo: "/lovable-uploads/6468597b-fa36-4cf3-9778-09ae793205da.png",
    id: "salesforce"
  },
  {
    name: "HubSpot",
    description: "Integrate with HubSpot's powerful CRM tools",
    logo: "/lovable-uploads/40aa3e6a-17f4-43e2-ba61-6016261df52e.png",
    id: "hubspot"
  },
  {
    name: "Zoho CRM",
    description: "Connect with Zoho's versatile CRM solution",
    logo: "/lovable-uploads/8da725ee-d154-4e63-80d8-7cb502aa0f04.png",
    id: "zoho"
  },
  {
    name: "Pipedrive",
    description: "Integrate with Pipedrive's sales-focused CRM",
    logo: "/lovable-uploads/1cb367f8-5ac6-4a45-bed8-0fadd012eda7.png",
    id: "pipedrive"
  },
  {
    name: "Monday.com",
    description: "Connect with Monday.com's flexible CRM platform",
    logo: "/lovable-uploads/4571d979-2073-44bf-81ea-cb5be9af87ab.png",
    id: "monday"
  },
];

export function CrmIntegrationDialog({
  open,
  onOpenChange,
  userId
}: CrmIntegrationDialogProps) {
  const [selectedCrm, setSelectedCrm] = useState<string | null>(null);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);

  const handleConnect = (crmId: string) => {
    setSelectedCrm(crmId);
    setWebhookDialogOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[700px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">CRM Integrations</DialogTitle>
            <p className="text-sm text-gray-500 mt-2">
              Connect your favorite CRM platform to streamline your lead management and sales processes. Choose from our supported integrations below.
            </p>
          </DialogHeader>
          <div className="mt-6 space-y-5">
            {crms.map((crm) => (
              <div
                key={crm.id}
                className="flex items-center justify-between p-5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <img 
                    src={crm.logo} 
                    alt={`${crm.name} logo`} 
                    className="w-10 h-10 object-contain"
                  />
                  <div>
                    <h3 className="font-medium text-lg">{crm.name}</h3>
                    <p className="text-sm text-gray-500">{crm.description}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-4"
                  onClick={() => handleConnect(crm.id)}
                >
                  Connect
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {selectedCrm && (
        <CrmWebhookDialog
          open={webhookDialogOpen}
          onOpenChange={setWebhookDialogOpen}
          crmType={selectedCrm}
          crmName={crms.find(crm => crm.id === selectedCrm)?.name || ""}
          userId={userId}
        />
      )}
    </>
  );
}
